import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Checkbox,
  Col,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Radio,
  Row,
  Select,
  Space,
  Statistic,
  Table,
  Tag,
  Upload,
  message
} from 'antd';
import { CalculatorOutlined, CloudUploadOutlined, DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import apiClient from '../services/apiClient';
import { useAuth } from '../context/AuthContext';
import './CommercialAssessments.css';

type Provider = 'on-premises' | 'aws' | 'azure' | 'gcp' | 'tencent';
type InputMode = 'quick' | 'manual' | 'csv' | 'connected';

type NormalizedResource = {
  sourceResourceId: string;
  name: string;
  category: 'compute' | 'block-storage' | 'object-storage' | 'database' | 'containers' | 'serverless' | 'network' |
    'nosql' | 'cache' | 'backup' | 'messaging' | 'cdn' | 'security' | 'data-warehouse' | 'observability';
  sourceType?: string;
  projectId?: string;
  region?: string;
  zone?: string;
  status?: string;
  machineType?: string;
  vcpu?: number;
  memoryGb?: number;
  architecture?: string;
  operatingSystem?: string;
  monthlyHours?: number;
  cpuAveragePercent?: number;
  cpuP95Percent?: number;
  observedHours?: number;
  diskSizeGb?: number;
  diskType?: string;
  storageClass?: string;
  databaseEngine?: string;
  highAvailability?: boolean;
  kubernetesNodeCount?: number;
  requestsPerMonth?: number;
  executionGbSeconds?: number;
  serviceType?: string;
  processedDataGb?: number;
  operationsPerMonth?: number;
  outboundDataGb?: number;
  retainedDataGb?: number;
  currentMonthlyCost?: number;
  currentCostSource?: string;
};

const providerLabels: Record<Provider, string> = {
  'on-premises': 'On-premises',
  aws: 'AWS',
  azure: 'Azure',
  gcp: 'GCP',
  tencent: 'Tencent Cloud'
};
const equivalenceLabels: Record<string, string> = {
  direct: 'Direta',
  approximate: 'Aproximada',
  architectural: 'Arquitetural'
};
const equivalenceColors: Record<string, string> = {
  direct: 'green',
  approximate: 'blue',
  architectural: 'gold'
};
const inputModeLabels: Record<InputMode, string> = {
  quick: 'Comparação rápida',
  manual: 'Editor manual',
  csv: 'Inventário CSV',
  connected: 'Conta conectada'
};
const geographyOptions = [
  { value: 'brazil', label: 'Brasil / São Paulo' },
  { value: 'north-america-east', label: 'América do Norte / Costa Leste' },
  { value: 'europe-west', label: 'Europa Ocidental / Frankfurt' }
];
const regionOptions: Record<Exclude<Provider, 'on-premises'>, Array<{ value: string; label: string }>> = {
  aws: [
    { value: 'sa-east-1', label: 'São Paulo (sa-east-1)' },
    { value: 'us-east-1', label: 'N. Virginia (us-east-1)' },
    { value: 'us-east-2', label: 'Ohio (us-east-2)' },
    { value: 'us-west-2', label: 'Oregon (us-west-2)' },
    { value: 'eu-central-1', label: 'Frankfurt (eu-central-1)' }
  ],
  azure: [
    { value: 'brazilsouth', label: 'Brazil South' },
    { value: 'eastus', label: 'East US' },
    { value: 'eastus2', label: 'East US 2' },
    { value: 'westeurope', label: 'West Europe' },
    { value: 'germanywestcentral', label: 'Germany West Central' }
  ],
  gcp: [
    { value: 'southamerica-east1', label: 'São Paulo (southamerica-east1)' },
    { value: 'us-east4', label: 'N. Virginia (us-east4)' },
    { value: 'us-central1', label: 'Iowa (us-central1)' },
    { value: 'europe-west1', label: 'Belgium (europe-west1)' },
    { value: 'europe-west3', label: 'Frankfurt (europe-west3)' }
  ],
  tencent: [
    { value: 'sa-saopaulo', label: 'São Paulo (sa-saopaulo)' },
    { value: 'na-ashburn', label: 'Virginia (na-ashburn)' },
    { value: 'na-siliconvalley', label: 'Silicon Valley (na-siliconvalley)' },
    { value: 'eu-frankfurt', label: 'Frankfurt (eu-frankfurt)' }
  ]
};
const equivalentRegions: Record<string, Partial<Record<Provider, string>>> = {
  brazil: { aws: 'sa-east-1', azure: 'brazilsouth', gcp: 'southamerica-east1', tencent: 'sa-saopaulo' },
  'north-america-east': { aws: 'us-east-1', azure: 'eastus', gcp: 'us-east4', tencent: 'na-ashburn' },
  'europe-west': { aws: 'eu-central-1', azure: 'westeurope', gcp: 'europe-west1', tencent: 'eu-frankfurt' }
};
const blankManualResource = (): NormalizedResource => ({
  sourceResourceId: `manual-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
  name: '',
  category: 'compute',
  monthlyHours: 730,
  architecture: 'x86_64',
  operatingSystem: 'linux'
});

const parseCsvLine = (line: string, delimiter = ';') => {
  const values: string[] = [];
  let value = '';
  let quoted = false;
  for (let index = 0; index < line.length; index += 1) {
    const char = line[index];
    if (char === '"') {
      if (quoted && line[index + 1] === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (char === delimiter && !quoted) {
      values.push(value);
      value = '';
    } else {
      value += char;
    }
  }
  values.push(value);
  return values;
};

const manualTemplateFields = [
  'sourceProvider',
  'resourceId',
  'name',
  'category',
  'region',
  'zone',
  'status',
  'machineType',
  'vcpu',
  'memoryGb',
  'architecture',
  'operatingSystem',
  'monthlyHours',
  'cpuAveragePercent',
  'cpuP95Percent',
  'diskType',
  'diskSizeGb',
  'storageClass',
  'databaseEngine',
  'highAvailability',
  'kubernetesNodeCount',
  'requestsPerMonth',
  'executionGbSeconds',
  'serviceType',
  'processedDataGb',
  'operationsPerMonth',
  'outboundDataGb',
  'retainedDataGb',
  'currentMonthlyCost',
  'currency'
];

const downloadInventoryTemplate = () => {
  const examples = [
    ['on-premises', 'vm-app-01', 'vm-app-01', 'compute', 'br-sao-paulo', '', 'running', '', '4', '16', 'x86_64', 'linux', '730', '25', '65', '', '', '', '', '', '', '', '', '', '', '1200', 'BRL'],
    ['on-premises', 'disk-app-01', 'disk-app-01', 'block-storage', 'br-sao-paulo', '', 'attached', '', '', '', '', '', '730', '', '', 'ssd', '500', '', '', '', '', '', '', '', '', '350', 'BRL'],
    ['on-premises', 'storage-backup', 'storage-backup', 'object-storage', 'br-sao-paulo', '', 'active', '', '', '', '', '', '730', '', '', '', '2000', 'standard', '', '', '', '', '', '', '', '180', 'BRL'],
    ['on-premises', 'db-app-01', 'db-app-01', 'database', 'br-sao-paulo', '', 'running', '', '4', '16', 'x86_64', 'linux', '730', '', '', '', '200', '', 'postgresql', 'false', '', '', '', '', '', '900', 'BRL'],
    ['on-premises', 'k8s-app-01', 'k8s-app-01', 'containers', 'br-sao-paulo', '', 'running', '', '4', '16', 'x86_64', 'linux', '730', '', '', '', '', '', '', '', '3', '', '', '', '', '1500', 'BRL'],
    ['on-premises', 'function-app', 'function-app', 'serverless', 'br-sao-paulo', '', 'active', '', '', '', '', '', '730', '', '', '', '', '', '', '', '', '1000000', '400000', '', '', '80', 'BRL'],
    ['on-premises', 'lb-app-01', 'lb-app-01', 'network', 'br-sao-paulo', '', 'active', '', '', '', '', '', '730', '', '', '', '', '', '', '', '', '', '', 'load-balancer', '500', '120', 'BRL']
  ].map(row => [...row.slice(0, -2), '', '', '', ...row.slice(-2)]);
  const escape = (value: string) => `"${String(value).replace(/"/g, '""')}"`;
  const content = [manualTemplateFields, ...examples]
    .map(row => row.map(escape).join(';'))
    .join('\r\n');
  const url = URL.createObjectURL(new Blob([`\uFEFF${content}`], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = 'modelo-inventario-calculadora-multicloud.csv';
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const CommercialAssessments: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.roles?.some(role => role.toLowerCase() === 'admin');
  const [form] = Form.useForm();
  const selectedTargetProviders = Form.useWatch('targetProviders', form) || [];
  const selectedSourceProvider = Form.useWatch('sourceProvider', form) || 'on-premises';
  const selectedCurrency = Form.useWatch('currency', form) || 'BRL';
  const selectedRegionMode = Form.useWatch('regionMode', form) || 'equivalent';
  const selectedGeography = Form.useWatch('geography', form) || 'brazil';
  const [assessments, setAssessments] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedAssessment, setSelectedAssessment] = useState<any>(null);
  const [detailResources, setDetailResources] = useState<NormalizedResource[]>([]);
  const [detailPagination, setDetailPagination] = useState({ page: 1, limit: 50, total: 0 });
  const [detailSearch, setDetailSearch] = useState('');
  const [detailClass, setDetailClass] = useState<string | undefined>();
  const [detailCategory, setDetailCategory] = useState<string | undefined>();
  const [calculation, setCalculation] = useState<any>(null);
  const [calculationLoading, setCalculationLoading] = useState(false);
  const [catalog, setCatalog] = useState<any>(null);
  const [catalogSyncing, setCatalogSyncing] = useState(false);
  const [sourceMode, setSourceMode] = useState<'import' | 'connected'>('import');
  const [inputMode, setInputMode] = useState<InputMode>('csv');
  const [manualResources, setManualResources] = useState<NormalizedResource[]>([blankManualResource()]);
  const [imported, setImported] = useState<{
    fileName: string;
    file: File;
    resources: NormalizedResource[];
    summary: any;
    sourceProvider: Provider;
    currency: string;
  } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [assessmentResponse, accountsResponse, catalogResponse] = await Promise.all([
        apiClient.get('/commercial/assessments'),
        apiClient.get('/cloud/accounts', { params: { limit: 100 } }),
        apiClient.get('/commercial/assessments/catalog/status').catch(() => ({ data: { catalog: null } }))
      ]);
      setAssessments(assessmentResponse.data?.assessments || []);
      setAccounts(accountsResponse.data?.accounts || []);
      setCatalog(catalogResponse.data?.catalog || null);
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Não foi possível carregar os levantamentos.');
    } finally {
      setLoading(false);
    }
  }, []);

  const synchronizeCatalog = async () => {
    setCatalogSyncing(true);
    try {
      await apiClient.post('/commercial/assessments/catalog/synchronize');
      message.success('Sincronização do catálogo enviada para a fila.');
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Não foi possível sincronizar o catálogo.');
    } finally {
      setCatalogSyncing(false);
    }
  };

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!assessments.some(item => ['awaiting-upload', 'queued', 'processing', 'calculating'].includes(item.status))) return;
    const timer = window.setInterval(async () => {
      try {
        const response = await apiClient.get('/commercial/assessments');
        setAssessments(response.data?.assessments || []);
      } catch {
        // Mantém os dados atuais; o próximo ciclo tenta novamente.
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [assessments]);

  useEffect(() => {
    if (!detailOpen || !selectedAssessment?.id || selectedAssessment.status !== 'calculating') return;
    const timer = window.setInterval(async () => {
      try {
        const response = await apiClient.get(`/commercial/assessments/${encodeURIComponent(selectedAssessment.id)}`);
        const nextAssessment = response.data?.assessment;
        if (!nextAssessment) return;
        setSelectedAssessment(nextAssessment);
        if (nextAssessment.status === 'comparison-ready') {
          const calculationResponse = await apiClient.get(`/commercial/assessments/${encodeURIComponent(nextAssessment.id)}/calculation`);
          setCalculation(calculationResponse.data?.calculation || null);
        }
      } catch {
        // O próximo ciclo tenta novamente sem fechar o detalhe atual.
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [detailOpen, selectedAssessment?.id, selectedAssessment?.status]);

  const accountOptions = useMemo(() => accounts.map(account => ({
    value: account.id,
    label: `${account.name} · ${String(account.provider || '').toUpperCase()}`
  })), [accounts]);

  const handleFile = async (file: File) => {
    setParsing(true);
    try {
      if (file.size > 250 * 1024 * 1024) throw new Error('O inventário deve possuir até 250 MB.');
      const preview = (await file.slice(0, 512 * 1024).text()).replace(/^\uFEFF/, '');
      const lines = preview.split(/\r?\n/).filter(Boolean);
      const header = parseCsvLine(lines[0] || '');
      const isXknow = header.includes('assetType') && header.includes('resourceId');
      const isManual = header.includes('sourceProvider') && header.includes('category');
      if (!isXknow && !isManual) throw new Error('Formato não reconhecido. Use o modelo da calculadora ou um CSV exportado pela XKnow.');
      let sourceProvider: Provider = 'gcp';
      let currency = 'BRL';
      if (isManual && lines[1]) {
        const firstRow = parseCsvLine(lines[1]);
        const positions = Object.fromEntries(header.map((field, index) => [field, index]));
        sourceProvider = (firstRow[positions.sourceProvider] || 'on-premises') as Provider;
        currency = firstRow[positions.currency] || 'BRL';
      }
      const result = {
        file,
        resources: [],
        sourceProvider,
        currency,
        summary: { total: 0, compute: 0, disks: 0, buckets: 0, awaitingProcessing: true }
      };
      setImported({ fileName: file.name, ...result });
      form.setFieldsValue({
        sourceProvider: result.sourceProvider,
        currency: result.currency
      });
      message.success('Arquivo validado e pronto para envio assíncrono.');
    } catch (error: any) {
      setImported(null);
      message.error(error?.message || 'Não foi possível interpretar o inventário.');
    } finally {
      setParsing(false);
    }
    return false;
  };

  const createAssessment = async () => {
    const values = await form.validateFields();
    if (inputMode === 'csv' && !imported) {
      message.error('Importe um inventário antes de continuar.');
      return;
    }
    if (['quick', 'manual'].includes(inputMode) && manualResources.some(resource => !resource.name.trim())) {
      message.error('Informe o nome de todos os recursos manuais.');
      return;
    }
    if (['quick', 'manual'].includes(inputMode) && manualResources.some(resource =>
      resource.category === 'compute' && (!resource.vcpu || !resource.memoryGb)
    )) {
      message.error('Máquina virtual exige vCPU e memória.');
      return;
    }
    if (['quick', 'manual'].includes(inputMode) && selectedSourceProvider === 'on-premises' &&
        manualResources.some(resource =>
          !resource.projectId?.trim() ||
          resource.currentMonthlyCost === null ||
          resource.currentMonthlyCost === undefined ||
          !Number.isFinite(Number(resource.currentMonthlyCost))
        )) {
      message.error('No cenário on-premises, informe o datacenter/ambiente e o custo mensal atual de cada recurso.');
      return;
    }
    if (['quick', 'manual'].includes(inputMode) && manualResources.some(resource =>
      ['block-storage', 'object-storage'].includes(resource.category) &&
      (!resource.diskSizeGb || resource.diskSizeGb <= 0)
    )) {
      message.error('Informe a capacidade em GB de todos os recursos de armazenamento.');
      return;
    }
    if (['quick', 'manual'].includes(inputMode) && manualResources.some(resource =>
      resource.category === 'database' &&
      (!resource.vcpu || !resource.memoryGb || !resource.diskSizeGb || !resource.databaseEngine)
    )) {
      message.error('Banco SQL exige engine, vCPU, memória e armazenamento.');
      return;
    }
    if (['quick', 'manual'].includes(inputMode) && manualResources.some(resource =>
      resource.category === 'containers' &&
      (!resource.kubernetesNodeCount || !resource.vcpu || !resource.memoryGb)
    )) {
      message.error('Kubernetes exige quantidade, vCPU e memória dos nós.');
      return;
    }
    if (['quick', 'manual'].includes(inputMode) && manualResources.some(resource =>
      resource.category === 'serverless' &&
      !resource.requestsPerMonth && !resource.executionGbSeconds
    )) {
      message.error('Serverless exige requisições ou GB-segundos mensais.');
      return;
    }
    if (['quick', 'manual'].includes(inputMode) && manualResources.some(resource =>
      ['nosql', 'backup', 'messaging', 'cdn', 'security', 'data-warehouse', 'observability'].includes(resource.category) &&
      !resource.operationsPerMonth && !resource.processedDataGb && !resource.outboundDataGb &&
      !resource.retainedDataGb && !resource.diskSizeGb
    )) {
      message.error('Informe ao menos uma unidade de consumo para o recurso arquitetural.');
      return;
    }
    setSaving(true);
    try {
      if (inputMode === 'csv' && imported) {
        const prepared = await apiClient.post('/commercial/assessments/uploads', {
          ...values,
          sourceMode: 'import',
          inputMode,
          sourceFileName: imported.fileName,
          sourceFileSize: imported.file.size
        });
        const uploadResponse = await fetch(prepared.data.uploadUrl, {
          method: 'PUT',
          headers: { 'Content-Type': 'text/csv' },
          body: imported.file
        });
        if (!uploadResponse.ok) throw new Error(`Falha no upload do inventário (${uploadResponse.status}).`);
        await apiClient.post(`/commercial/assessments/${encodeURIComponent(prepared.data.assessment.id)}/process`);
        message.success('Inventário enviado. O processamento continuará em segundo plano.');
      } else {
        const created = await apiClient.post('/commercial/assessments', {
          ...values,
          sourceMode: inputMode === 'connected' ? 'connected' : 'import',
          inputMode,
          resources: ['quick', 'manual'].includes(inputMode) ? manualResources : []
        });
        if (inputMode === 'connected') {
          await apiClient.post(`/commercial/assessments/${encodeURIComponent(created.data.assessment.id)}/process-connected`);
          message.success('Inventário conectado enviado para processamento.');
        } else {
          message.success('Levantamento comercial criado.');
        }
      }
      setModalOpen(false);
      setImported(null);
      form.resetFields();
      await load();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Não foi possível criar o levantamento.');
    } finally {
      setSaving(false);
    }
  };

  const loadDetailResources = useCallback(async (
    assessmentId: string,
    page = 1,
    limit = 50,
    search = '',
    comparisonClass?: string,
    category?: string
  ) => {
    const response = await apiClient.get(`/commercial/assessments/${encodeURIComponent(assessmentId)}/resources`, {
      params: { page, limit, search: search || undefined, comparisonClass, category }
    });
    setDetailResources(response.data?.items || []);
    setDetailPagination({
      page: response.data?.pagination?.page || page,
      limit: response.data?.pagination?.limit || limit,
      total: response.data?.pagination?.total || 0
    });
  }, []);

  const openAssessment = async (record: any) => {
    setDetailOpen(true);
    setDetailLoading(true);
    setSelectedAssessment(record);
    setDetailResources([]);
    setDetailSearch('');
    setDetailClass(undefined);
    setDetailCategory(undefined);
    setCalculation(null);
    try {
      const response = await apiClient.get(`/commercial/assessments/${encodeURIComponent(record.id)}`);
      const assessment = response.data?.assessment || record;
      setSelectedAssessment(assessment);
      if (assessment.resources?.length) {
        setDetailResources(assessment.resources);
        setDetailPagination({ page: 1, limit: 50, total: assessment.resources.length });
      }
      if (['inventory-ready', 'calculating', 'comparison-ready', 'calculation-failed'].includes(assessment.status) && assessment.resourcePartitions?.length) {
        await loadDetailResources(assessment.id);
      }
      if (assessment.status === 'comparison-ready') {
        const calculationResponse = await apiClient.get(`/commercial/assessments/${encodeURIComponent(assessment.id)}/calculation`);
        setCalculation(calculationResponse.data?.calculation || null);
      }
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Não foi possível abrir o levantamento.');
    } finally {
      setDetailLoading(false);
    }
  };

  const calculateAssessment = async () => {
    if (!selectedAssessment?.id) return;
    setCalculationLoading(true);
    try {
      await apiClient.post(`/commercial/assessments/${encodeURIComponent(selectedAssessment.id)}/calculate`);
      setCalculation(null);
      setSelectedAssessment((current: any) => ({
        ...current,
        status: 'calculating',
        calculationProgress: { percent: 5, message: 'Comparação aguardando processamento' }
      }));
      message.success('Comparação enviada para processamento.');
      await load();
    } catch (error: any) {
      message.error(error?.response?.data?.message || 'Não foi possível iniciar a comparação.');
    } finally {
      setCalculationLoading(false);
    }
  };

  const columns = [
    { title: 'Levantamento', dataIndex: 'name', key: 'name', render: (value: string, row: any) => <><strong>{value}</strong><small className="commercial-assessments__secondary">{row.prospectName}</small></> },
    { title: 'Origem', dataIndex: 'sourceProvider', key: 'sourceProvider', render: (value: Provider, row: any) => <><Tag>{providerLabels[value] || value}</Tag><small className="commercial-assessments__secondary">{inputModeLabels[row.inputMode as InputMode] || 'Levantamento'}</small></> },
    { title: 'Destinos', dataIndex: 'targetProviders', key: 'targetProviders', render: (values: Provider[]) => <Space wrap>{(values || []).map(value => <Tag color="blue" key={value}>{providerLabels[value]}</Tag>)}</Space> },
    { title: 'Calculáveis', dataIndex: 'resourcesStored', key: 'resourcesStored' },
    { title: 'Status', dataIndex: 'status', key: 'status', width: 210, render: (value: string, row: any) => (
      value === 'comparison-ready'
        ? <Tag color="blue">Comparação pronta</Tag>
        : value === 'inventory-ready'
        ? <Tag color="green">Inventário pronto</Tag>
        : value === 'calculation-failed'
          ? <><Tag color="red">Falha na comparação</Tag><small className="commercial-assessments__secondary">{row.calculationProgress?.message}</small></>
        : value === 'failed'
          ? <Tag color="red">Falha no processamento</Tag>
          : ['awaiting-upload', 'queued', 'processing', 'calculating'].includes(value)
            ? <div><Progress percent={Number((value === 'calculating' ? row.calculationProgress : row.progress)?.percent || 0)} size="small" /><small className="commercial-assessments__secondary">{(value === 'calculating' ? row.calculationProgress : row.progress)?.message || 'Processando'}</small></div>
            : <Tag>Rascunho</Tag>
    ) },
    { title: 'Atualizado', dataIndex: 'updatedAt', key: 'updatedAt', render: (value: string) => new Date(value).toLocaleString('pt-BR') },
    { title: 'Expira', dataIndex: 'expiresAt', key: 'expiresAt', render: (value: number) => {
      const remainingHours = Math.max(0, Math.ceil((Number(value) * 1000 - Date.now()) / 3600000));
      return <Tag color={remainingHours <= 24 ? 'orange' : 'default'}>{remainingHours > 24 ? `${Math.ceil(remainingHours / 24)} dias` : `${remainingHours}h`}</Tag>;
    } },
    { title: 'Ações', key: 'actions', render: (_: unknown, row: any) => <Button size="small" onClick={() => openAssessment(row)}>Abrir</Button> }
  ];

  return (
    <div className="commercial-assessments">
      <div className="commercial-assessments__head">
        <div>
          <span>Área comercial</span>
          <h1>Levantamentos multicloud</h1>
          <p>Importe inventários de prospects ou utilize contas conectadas para preparar comparações.</p>
        </div>
        <Space wrap>
          <Button icon={<DownloadOutlined />} onClick={downloadInventoryTemplate}>Baixar modelo de inventário</Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={() => {
            form.resetFields();
            form.setFieldsValue({
              sourceProvider: 'on-premises',
              targetProviders: ['aws', 'azure', 'gcp', 'tencent'],
              currency: 'BRL',
              tencentDiscountPercent: 0,
              tencentChannelDiscounts: { aws: 0, azure: 0, gcp: 0 },
              regionMode: 'equivalent',
              geography: 'brazil',
              targetRegions: {
                aws: 'sa-east-1',
                azure: 'brazilsouth',
                gcp: 'southamerica-east1',
                tencent: 'sa-saopaulo'
              }
            });
            setSourceMode('import');
            setInputMode('csv');
            setManualResources([blankManualResource()]);
            setImported(null);
            setModalOpen(true);
          }}>Novo levantamento</Button>
        </Space>
      </div>

      <Card>
        <Alert
          type={!catalog ? 'warning' : Object.values(catalog.providers || {}).some((provider: any) => provider.status === 'failed') ? 'warning' : 'success'}
          showIcon
          message={catalog ? `Catálogo de preços atualizado em ${new Date(catalog.generatedAt).toLocaleString('pt-BR')}` : 'Catálogo local ainda não sincronizado'}
          description={catalog
            ? `${Number(catalog.entries || 0).toLocaleString('pt-BR')} preços Compute disponíveis · versão ${catalog.version}`
            : 'A calculadora poderá consultar as APIs diretamente até a primeira sincronização.'}
          action={isAdmin ? <Button size="small" loading={catalogSyncing} onClick={synchronizeCatalog}>Sincronizar agora</Button> : undefined}
        />
        <Table rowKey="id" loading={loading} columns={columns} dataSource={assessments} locale={{ emptyText: <Empty description="Nenhum levantamento comercial criado." /> }} />
      </Card>

      <Modal title="Novo levantamento multicloud" open={modalOpen} onCancel={() => setModalOpen(false)} onOk={createAssessment} confirmLoading={saving} okText="Criar levantamento" width={820}>
        <Form form={form} layout="vertical">
          <Row gutter={16}>
            <Col span={12}><Form.Item name="name" label="Nome do levantamento" rules={[{ required: true }]}><Input placeholder="Migração ambiente principal" /></Form.Item></Col>
            <Col span={12}><Form.Item name="prospectName" label="Cliente ou prospect" rules={[{ required: true }]}><Input placeholder="Empresa" /></Form.Item></Col>
          </Row>
          <Form.Item label="Como deseja informar o ambiente?">
            <Radio.Group value={inputMode} onChange={event => {
              const nextMode = event.target.value as InputMode;
              setInputMode(nextMode);
              setSourceMode(nextMode === 'connected' ? 'connected' : 'import');
              setImported(null);
              if (nextMode === 'quick') setManualResources([blankManualResource()]);
            }}>
              <Radio.Button value="quick">Comparação rápida</Radio.Button>
              <Radio.Button value="manual">Preencher na tela</Radio.Button>
              <Radio.Button value="csv">Importar CSV</Radio.Button>
              <Radio.Button value="connected">Conta conectada</Radio.Button>
            </Radio.Group>
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="sourceProvider" label="Ambiente atual" rules={[{ required: true }]}>
                <Select disabled={sourceMode === 'connected'} options={(Object.keys(providerLabels) as Provider[]).map(value => ({ value, label: providerLabels[value] }))} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="currency" label="Moeda"><Select options={[{ value: 'BRL', label: 'BRL — Real' }, { value: 'USD', label: 'USD — Dólar' }]} /></Form.Item>
            </Col>
          </Row>
          {selectedCurrency === 'BRL' && (
            <Form.Item
              name="exchangeRateUsdToBrl"
              label="Cotação USD/BRL para a comparação"
              extra="Informe a taxa que deve ser usada no estudo. A XKnow preservará o preço público original em USD e mostrará o equivalente em BRL."
              rules={[{ required: true, message: 'Informe a cotação USD/BRL.' }]}
            >
              <InputNumber min={0.01} precision={4} step={0.01} style={{ width: '100%' }} placeholder="Ex.: 5,50" />
            </Form.Item>
          )}
          {inputMode === 'connected' ? (
            <Form.Item name="connectedAccountId" label="Conta conectada" rules={[{ required: true }]}>
              <Select showSearch optionFilterProp="label" options={accountOptions} placeholder="Selecione uma conta" onChange={id => {
                const account = accounts.find(item => item.id === id);
                if (account?.provider) form.setFieldValue('sourceProvider', account.provider);
              }} />
            </Form.Item>
          ) : inputMode === 'csv' ? (
            <Form.Item label="Arquivo de inventário" required>
              <Space wrap>
                <Upload accept=".csv" maxCount={1} beforeUpload={handleFile} showUploadList={false}>
                  <Button icon={<CloudUploadOutlined />} loading={parsing}>Selecionar CSV</Button>
                </Upload>
                <Button icon={<DownloadOutlined />} onClick={downloadInventoryTemplate}>Baixar modelo</Button>
              </Space>
              {imported && (
                <Alert
                  className="commercial-assessments__import-summary"
                  type="success"
                  showIcon
                  message={imported.fileName}
                  description={`${(imported.file.size / 1024 / 1024).toLocaleString('pt-BR', { maximumFractionDigits: 2 })} MB • ${providerLabels[imported.sourceProvider]} • será processado em segundo plano`}
                />
              )}
            </Form.Item>
          ) : (
            <div className="commercial-assessments__manual-list">
              {manualResources.map((resource, index) => (
                <Card
                  size="small"
                  key={resource.sourceResourceId}
                  title={inputMode === 'quick' ? 'Recurso para comparação' : `Recurso ${index + 1}`}
                  extra={inputMode === 'manual' && manualResources.length > 1
                    ? <Button danger type="link" onClick={() => setManualResources(current => current.filter((_, itemIndex) => itemIndex !== index))}>Remover</Button>
                    : null}
                >
                  <Row gutter={12}>
                    <Col span={8}><label>Nome<Input value={resource.name} onChange={event => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, name: event.target.value } : item))} /></label></Col>
                    <Col span={6}><label>Categoria<Select value={resource.category} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? {
                      ...item,
                      category: value,
                      ...(value === 'block-storage' && !item.diskType ? { diskType: 'ssd' } : {}),
                      ...(value === 'object-storage' && !item.storageClass ? { storageClass: 'standard' } : {}),
                      ...(value === 'database' ? {
                        databaseEngine: item.databaseEngine || 'postgresql',
                        vcpu: item.vcpu || 2,
                        memoryGb: item.memoryGb || 8,
                        diskSizeGb: item.diskSizeGb || 100
                      } : {}),
                      ...(value === 'containers' ? {
                        kubernetesNodeCount: item.kubernetesNodeCount || 3,
                        vcpu: item.vcpu || 2,
                        memoryGb: item.memoryGb || 8,
                        operatingSystem: item.operatingSystem || 'linux'
                      } : {}),
                      ...(value === 'serverless' ? {
                        requestsPerMonth: item.requestsPerMonth || 1_000_000,
                        executionGbSeconds: item.executionGbSeconds || 400_000
                      } : {}),
                      ...(value === 'network' ? {
                        serviceType: item.serviceType || 'load-balancer',
                        processedDataGb: item.processedDataGb || 100
                      } : {}),
                      ...(value === 'cache' ? {
                        vcpu: item.vcpu || 2,
                        memoryGb: item.memoryGb || 8
                      } : {}),
                      ...(['nosql', 'backup', 'messaging', 'cdn', 'security', 'data-warehouse', 'observability'].includes(value) ? {
                        operationsPerMonth: item.operationsPerMonth || 1_000_000,
                        processedDataGb: item.processedDataGb || 100,
                        outboundDataGb: item.outboundDataGb || 100,
                        retainedDataGb: item.retainedDataGb || 100
                      } : {})
                    } : item))} options={[
                      { value: 'compute', label: 'Máquina virtual' },
                      { value: 'block-storage', label: 'Disco em bloco' },
                      { value: 'object-storage', label: 'Object storage' },
                      { value: 'database', label: 'Banco SQL gerenciado' },
                      { value: 'containers', label: 'Kubernetes gerenciado' },
                      { value: 'serverless', label: 'Serverless / Functions' },
                      { value: 'network', label: 'Rede / Load Balancer' },
                      { value: 'nosql', label: 'Banco NoSQL' },
                      { value: 'cache', label: 'Cache gerenciado' },
                      { value: 'backup', label: 'Backup' },
                      { value: 'messaging', label: 'Mensageria / Filas' },
                      { value: 'cdn', label: 'CDN' },
                      { value: 'security', label: 'Firewall / VPN' },
                      { value: 'data-warehouse', label: 'Data warehouse' },
                      { value: 'observability', label: 'Observabilidade' }
                    ]} /></label></Col>
                    <Col span={5}><label>Região<Input value={resource.region} placeholder="Brasil/São Paulo" onChange={event => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, region: event.target.value } : item))} /></label></Col>
                    <Col span={5}><label>Horas/mês<InputNumber min={1} max={744} value={resource.monthlyHours} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, monthlyHours: Number(value || 730) } : item))} /></label></Col>
                  </Row>
                  <Row gutter={12}>
                    <Col span={12}><label>{selectedSourceProvider === 'on-premises' ? 'Datacenter / ambiente' : 'Projeto / conta / subscription'}<Input value={resource.projectId} placeholder={selectedSourceProvider === 'on-premises' ? 'DC São Paulo / Produção' : 'Identificador do ambiente'} onChange={event => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, projectId: event.target.value } : item))} /></label></Col>
                    <Col span={12}><label>Custo mensal atual<InputNumber min={0} precision={2} value={resource.currentMonthlyCost} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? {
                      ...item,
                      currentMonthlyCost: value === null ? undefined : Number(value),
                      currentCostSource: 'manual-monthly-cost'
                    } : item))} /></label></Col>
                  </Row>
                  {resource.category === 'compute' && (
                    <>
                      <Row gutter={12}>
                        <Col span={5}><label>vCPU<InputNumber min={1} value={resource.vcpu} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, vcpu: Number(value || 0) } : item))} /></label></Col>
                        <Col span={5}><label>Memória GB<InputNumber min={0.5} step={0.5} value={resource.memoryGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, memoryGb: Number(value || 0) } : item))} /></label></Col>
                        <Col span={5}><label>Sistema<Select value={resource.operatingSystem} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, operatingSystem: value } : item))} options={[{ value: 'linux', label: 'Linux' }, { value: 'windows', label: 'Windows' }]} /></label></Col>
                        <Col span={5}><label>CPU média %<InputNumber min={0} max={100} value={resource.cpuAveragePercent} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, cpuAveragePercent: Number(value || 0) } : item))} /></label></Col>
                        <Col span={4}><label>CPU P95 %<InputNumber min={0} max={100} value={resource.cpuP95Percent} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, cpuP95Percent: Number(value || 0) } : item))} /></label></Col>
                      </Row>
                      <Row gutter={12}>
                        <Col span={12}><label>Disco local/anexado GB<InputNumber min={0} value={resource.diskSizeGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, diskSizeGb: Number(value || 0) } : item))} /></label></Col>
                        <Col span={12}><label>Tipo do disco<Select allowClear value={resource.diskType} style={{ width: '100%' }} placeholder="Selecione" onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, diskType: value } : item))} options={[{ value: 'ssd', label: 'SSD' }, { value: 'hdd', label: 'HDD' }]} /></label></Col>
                      </Row>
                    </>
                  )}
                  {['block-storage', 'object-storage'].includes(resource.category) && (
                    <Row gutter={12}>
                      <Col span={8}><label>Capacidade GB<InputNumber min={1} value={resource.diskSizeGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, diskSizeGb: Number(value || 0) } : item))} /></label></Col>
                      <Col span={8}><label>Classe/tipo<Select
                        value={resource.category === 'block-storage' ? resource.diskType : resource.storageClass}
                        style={{ width: '100%' }}
                        placeholder="Selecione"
                        onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? {
                          ...item,
                          ...(resource.category === 'block-storage' ? { diskType: value } : { storageClass: value })
                        } : item))}
                        options={resource.category === 'block-storage'
                          ? [{ value: 'ssd', label: 'SSD' }, { value: 'hdd', label: 'HDD' }]
                          : [{ value: 'standard', label: 'Acesso frequente' }, { value: 'infrequent-access', label: 'Acesso infrequente' }]}
                      /></label></Col>
                    </Row>
                  )}
                  {resource.category === 'database' && (
                    <>
                      <Row gutter={12}>
                        <Col span={5}><label>Engine<Select value={resource.databaseEngine} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, databaseEngine: value } : item))} options={[
                          { value: 'postgresql', label: 'PostgreSQL' },
                          { value: 'mysql', label: 'MySQL' }
                        ]} /></label></Col>
                        <Col span={5}><label>vCPU<InputNumber min={1} value={resource.vcpu} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, vcpu: Number(value || 0) } : item))} /></label></Col>
                        <Col span={5}><label>Memória GB<InputNumber min={1} value={resource.memoryGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, memoryGb: Number(value || 0) } : item))} /></label></Col>
                        <Col span={5}><label>Armazenamento GB<InputNumber min={10} value={resource.diskSizeGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, diskSizeGb: Number(value || 0) } : item))} /></label></Col>
                        <Col span={4}><label>Alta disponibilidade<Checkbox checked={resource.highAvailability} onChange={event => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, highAvailability: event.target.checked } : item))}>Ativar</Checkbox></label></Col>
                      </Row>
                    </>
                  )}
                  {resource.category === 'containers' && (
                    <Row gutter={12}>
                      <Col span={6}><label>Nós<InputNumber min={1} value={resource.kubernetesNodeCount} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, kubernetesNodeCount: Number(value || 1) } : item))} /></label></Col>
                      <Col span={6}><label>vCPU por nó<InputNumber min={1} value={resource.vcpu} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, vcpu: Number(value || 0) } : item))} /></label></Col>
                      <Col span={6}><label>Memória GB por nó<InputNumber min={1} value={resource.memoryGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, memoryGb: Number(value || 0) } : item))} /></label></Col>
                      <Col span={6}><label>Sistema<Select value={resource.operatingSystem} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, operatingSystem: value } : item))} options={[{ value: 'linux', label: 'Linux' }, { value: 'windows', label: 'Windows' }]} /></label></Col>
                    </Row>
                  )}
                  {resource.category === 'serverless' && (
                    <Row gutter={12}>
                      <Col span={12}><label>Requisições por mês<InputNumber min={0} value={resource.requestsPerMonth} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, requestsPerMonth: Number(value || 0) } : item))} /></label></Col>
                      <Col span={12}><label>GB-segundos por mês<InputNumber min={0} value={resource.executionGbSeconds} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, executionGbSeconds: Number(value || 0) } : item))} /></label></Col>
                    </Row>
                  )}
                  {resource.category === 'network' && (
                    <Row gutter={12}>
                      <Col span={8}><label>Serviço<Select value={resource.serviceType} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, serviceType: value } : item))} options={[{ value: 'load-balancer', label: 'Load Balancer' }]} /></label></Col>
                      <Col span={8}><label>Horas por mês<InputNumber min={1} max={744} value={resource.monthlyHours} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, monthlyHours: Number(value || 730) } : item))} /></label></Col>
                      <Col span={8}><label>Dados processados GB<InputNumber min={0} value={resource.processedDataGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, processedDataGb: Number(value || 0) } : item))} /></label></Col>
                    </Row>
                  )}
                  {resource.category === 'cache' && (
                    <Row gutter={12}>
                      <Col span={12}><label>vCPU<InputNumber min={1} value={resource.vcpu} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, vcpu: Number(value || 0) } : item))} /></label></Col>
                      <Col span={12}><label>Memória GB<InputNumber min={1} value={resource.memoryGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, memoryGb: Number(value || 0) } : item))} /></label></Col>
                    </Row>
                  )}
                  {['nosql', 'backup', 'messaging', 'cdn', 'security', 'data-warehouse', 'observability'].includes(resource.category) && (
                    <>
                      <Alert
                        type="info"
                        showIcon
                        message="Equivalência arquitetural"
                        description="A comparação normaliza o consumo entre serviços funcionalmente equivalentes; não representa correspondência de SKU 1:1."
                      />
                      <Row gutter={12}>
                        <Col span={6}><label>Operações/mês<InputNumber min={0} value={resource.operationsPerMonth} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, operationsPerMonth: Number(value || 0) } : item))} /></label></Col>
                        <Col span={6}><label>Dados processados GB<InputNumber min={0} value={resource.processedDataGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, processedDataGb: Number(value || 0) } : item))} /></label></Col>
                        <Col span={6}><label>Saída de dados GB<InputNumber min={0} value={resource.outboundDataGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, outboundDataGb: Number(value || 0) } : item))} /></label></Col>
                        <Col span={6}><label>Dados retidos GB<InputNumber min={0} value={resource.retainedDataGb} style={{ width: '100%' }} onChange={value => setManualResources(current => current.map((item, itemIndex) => itemIndex === index ? { ...item, retainedDataGb: Number(value || 0) } : item))} /></label></Col>
                      </Row>
                    </>
                  )}
                </Card>
              ))}
              {inputMode === 'manual' && <Button onClick={() => setManualResources(current => [...current, blankManualResource()])}>Adicionar recurso</Button>}
            </div>
          )}
          <Form.Item name="targetProviders" label="Clouds que serão comparadas" rules={[{ required: true }]}>
            <Checkbox.Group options={(['aws', 'azure', 'gcp', 'tencent'] as Provider[]).map(value => ({ value, label: providerLabels[value] }))} />
          </Form.Item>
          <Form.Item name="regionMode" label="Como deseja escolher as regiões?">
            <Radio.Group>
              <Radio value="equivalent">Mesma geografia em todas as clouds</Radio>
              <Radio value="custom">Escolher uma região por cloud</Radio>
            </Radio.Group>
          </Form.Item>
          {selectedRegionMode === 'equivalent' ? (
            <>
              <Form.Item
                name="geography"
                label="Geografia de destino"
                extra="O sistema selecionará a região oficial equivalente em cada cloud."
                rules={[{ required: true }]}
              >
                <Select options={geographyOptions} />
              </Form.Item>
              <Alert
                type="info"
                showIcon
                message="Regiões que serão usadas na comparação"
                description={
                  <Space wrap>
                    {(selectedTargetProviders as Array<Exclude<Provider, 'on-premises'>>).map(provider => (
                      <Tag key={provider}>
                        {providerLabels[provider]}: {equivalentRegions[selectedGeography]?.[provider] || 'não disponível'}
                      </Tag>
                    ))}
                  </Space>
                }
              />
            </>
          ) : (
            <Row gutter={12}>
              {(selectedTargetProviders as Array<Exclude<Provider, 'on-premises'>>).map(provider => (
                <Col xs={24} md={12} key={provider}>
                  <Form.Item
                    name={['targetRegions', provider]}
                    label={`Região ${providerLabels[provider]}`}
                    rules={[{ required: true }]}
                  >
                    <Select showSearch optionFilterProp="label" options={regionOptions[provider]} />
                  </Form.Item>
                </Col>
              ))}
            </Row>
          )}
          {selectedTargetProviders.includes('tencent') && (
            <Form.Item
              name="tencentDiscountPercent"
              label="Desconto negociado sobre Tencent Cloud"
              extra="Aplicado ao total da infraestrutura Tencent Cloud. O preço público continuará registrado para auditoria."
              rules={[{ type: 'number', min: 0, max: 100, message: 'Informe um percentual entre 0 e 100.' }]}
            >
              <InputNumber min={0} max={100} precision={2} addonAfter="%" style={{ width: 220 }} />
            </Form.Item>
          )}
          {(selectedTargetProviders as Provider[]).some(provider => ['aws', 'azure', 'gcp'].includes(provider)) && (
            <Card size="small" title="Condições via parceria Tencent (opcional)">
              <p className="commercial-assessments__secondary">
                Informe o desconto comercial sobre o preço público de cada hyperscaler. Os dois valores serão apresentados no relatório.
              </p>
              <Row gutter={12}>
                {(selectedTargetProviders as Array<Exclude<Provider, 'on-premises'>>)
                  .filter(provider => ['aws', 'azure', 'gcp'].includes(provider))
                  .map(provider => (
                    <Col xs={24} md={8} key={provider}>
                      <Form.Item
                        name={['tencentChannelDiscounts', provider]}
                        label={`${providerLabels[provider]} via Tencent`}
                        rules={[{ type: 'number', min: 0, max: 100, message: 'Use um percentual entre 0 e 100.' }]}
                      >
                        <InputNumber min={0} max={100} precision={2} addonAfter="%" style={{ width: '100%' }} />
                      </Form.Item>
                    </Col>
                  ))}
              </Row>
            </Card>
          )}
          <Alert
            type="info"
            showIcon
            message="7 categorias com cálculo disponível"
            description="Compute, disco em bloco, object storage, banco SQL, Kubernetes, serverless e load balancer já geram comparação. NoSQL, cache, backup, mensageria, CDN, firewall/VPN e data warehouse serão adicionados somente quando seus contratos de consumo e preços estiverem implementados."
          />
        </Form>
      </Modal>

      <Modal
        title={selectedAssessment?.name || 'Levantamento'}
        open={detailOpen}
        onCancel={() => setDetailOpen(false)}
        footer={<Space>
          <Button onClick={() => setDetailOpen(false)}>Fechar</Button>
          <Button
            type="primary"
            icon={<CalculatorOutlined />}
            loading={calculationLoading}
            disabled={!['inventory-ready', 'comparison-ready', 'calculation-failed'].includes(selectedAssessment?.status)}
            onClick={calculateAssessment}
          >
            {selectedAssessment?.status === 'comparison-ready' ? 'Recalcular comparação' : 'Gerar comparação'}
          </Button>
        </Space>}
        width="92vw"
        loading={detailLoading}
      >
        {selectedAssessment && (
          <div className="commercial-assessments__detail">
            <Alert
              type={['inventory-ready', 'comparison-ready'].includes(selectedAssessment.status) ? 'success' : selectedAssessment.status === 'calculation-failed' ? 'error' : 'info'}
              showIcon
              message={selectedAssessment.prospectName}
              description={`${providerLabels[selectedAssessment.sourceProvider as Provider] || selectedAssessment.sourceProvider} → ${(selectedAssessment.targetProviders || []).map((provider: Provider) => providerLabels[provider]).join(', ')}`}
            />
            {selectedAssessment.status === 'calculating' && (
              <Card size="small" title="Calculando comparação multicloud">
                <Progress
                  percent={Number(selectedAssessment.calculationProgress?.percent || 0)}
                  status="active"
                />
                <span>{selectedAssessment.calculationProgress?.message || 'Consultando catálogos oficiais'}</span>
              </Card>
            )}
            {selectedAssessment.status === 'calculation-failed' && (
              <Alert
                type="error"
                showIcon
                message="A comparação não foi concluída"
                description={selectedAssessment.calculationProgress?.message || 'Tente novamente ou verifique a configuração dos catálogos.'}
              />
            )}
            {calculation?.summary?.providerTotals && (
              <Card title="Comparação mensal por cloud" size="small">
                {calculation.summary.comparisonStatus === 'partial' && (
                  <Alert
                    type="warning"
                    showIcon
                    message="Comparação parcial"
                    description={calculation.summary.comparisonWarning}
                    style={{ marginBottom: 12 }}
                  />
                )}
                <Row gutter={[12, 12]}>
                  {(selectedAssessment.targetProviders || []).map((provider: Provider) => {
                    const total = calculation.summary.providerTotals[provider] || {};
                    return (
                      <Col xs={24} sm={12} xl={6} key={provider}>
                        <Card size="small" className="commercial-assessments__provider-result">
                          <Tag color={total.error ? 'red' : 'blue'}>{providerLabels[provider]}</Tag>
                          {total.error ? (
                            <Alert type="error" showIcon={false} message="Preço indisponível" description={total.error} />
                          ) : (
                            <>
                              <Statistic
                                title={provider === 'tencent' && Number(total.discountPercent || 0) > 0 ? 'Total negociado' : 'Preço público mensal'}
                                value={Number(total.finalTotal || 0)}
                                precision={2}
                                prefix={total.currency === 'BRL' ? 'R$' : 'US$'}
                              />
                              {total.convertedFinalTotal != null && total.convertedCurrency !== total.currency && (
                                <small>
                                  Equivalente: {Number(total.convertedFinalTotal).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: total.convertedCurrency
                                  })}
                                </small>
                              )}
                              {total.differenceFromCurrent != null && (
                                <Tag color={Number(total.differenceFromCurrent) <= 0 ? 'green' : 'orange'}>
                                  {Number(total.differenceFromCurrent) <= 0 ? 'Economia' : 'Aumento'} de{' '}
                                  {Math.abs(Number(total.differenceFromCurrent)).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: total.convertedCurrency || selectedAssessment.currency || 'BRL'
                                  })}
                                  {' '}({Math.abs(Number(total.savingsPercent || 0)).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%)
                                </Tag>
                              )}
                              {Number(total.discountPercent || 0) > 0 && (
                                <small>
                                  Base {Number(total.baseTotal || 0).toLocaleString('pt-BR', { style: 'currency', currency: total.currency || 'USD' })}
                                  {' '}· desconto {Number(total.discountPercent).toLocaleString('pt-BR')}%
                                </small>
                              )}
                              {total.channel && (
                                <div className="commercial-assessments__channel-price">
                                  <Statistic
                                    title="Via parceria Tencent"
                                    value={Number(total.channel.finalTotal || 0)}
                                    precision={2}
                                    prefix={total.currency === 'BRL' ? 'R$' : 'US$'}
                                  />
                                  <small>
                                    Economia de {Number(total.channel.discountAmount || 0).toLocaleString('pt-BR', {
                                      style: 'currency',
                                      currency: total.currency || 'USD'
                                    })} ({Number(total.channel.discountPercent || 0).toLocaleString('pt-BR')}%)
                                  </small>
                                </div>
                              )}
                              <small>{Number(total.resourcesPriced || 0)} recursos precificados</small>
                              {total.comparableToCurrent === false && Number(total.currentMonthlyCost || 0) > 0 && (
                                <small>Economia não calculada: cobertura do custo atual de {Number(total.financialCoveragePercent || 0).toFixed(2)}%.</small>
                              )}
                            </>
                          )}
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
                {(calculation.resources || []).some((resource: any) =>
                  ['database', 'containers', 'serverless', 'network', 'nosql', 'cache', 'backup', 'messaging', 'cdn', 'security', 'data-warehouse', 'observability'].includes(resource.category)
                ) && (
                  <Alert
                    type="info"
                    showIcon
                    message="Estimativa composta e auditável"
                    description="Serviços gerenciados usam componentes públicos ou unidades de consumo normalizadas. O resultado identifica comparações aproximadas e arquiteturais; a metodologia e os componentes permanecem registrados."
                  />
                )}
                <Table
                  size="small"
                  rowKey={(row: any) => `${row.sourceResourceId}-${row.provider}`}
                  pagination={{ pageSize: 10, hideOnSinglePage: true }}
                  dataSource={(calculation.resources || []).flatMap((resource: any) =>
                    Object.entries(resource.providers || {}).map(([provider, result]: [string, any]) => ({
                      ...result,
                      sourceResourceId: resource.sourceResourceId,
                      resourceName: resource.name,
                      category: resource.category,
                      provider
                    }))
                  )}
                  columns={[
                    { title: 'Recurso', dataIndex: 'resourceName', key: 'resourceName' },
                    { title: 'Categoria', dataIndex: 'category', key: 'category' },
                    {
                      title: 'Cloud',
                      dataIndex: 'provider',
                      key: 'provider',
                      render: value => providerLabels[value as Provider] || value
                    },
                    {
                      title: 'Equivalência',
                      dataIndex: 'equivalenceType',
                      key: 'equivalenceType',
                      render: (value, row: any) => row.status === 'priced'
                        ? <Tag color={equivalenceColors[value] || 'default'}>{equivalenceLabels[value] || value}</Tag>
                        : <Tag>Sem preço</Tag>
                    },
                    {
                      title: 'Compatibilidade',
                      dataIndex: 'compatibilityPercent',
                      key: 'compatibilityPercent',
                      render: value => value == null ? '—' : `${Number(value)}%`
                    },
                    {
                      title: 'Preço mensal',
                      key: 'monthlyPrice',
                      render: (_value, row: any) => row.status === 'priced'
                        ? Number(row.quote?.monthlyPrice || 0).toLocaleString('pt-BR', {
                            style: 'currency',
                            currency: row.quote?.currency || 'USD'
                          })
                        : row.reason || 'Indisponível'
                    }
                  ]}
                />
                {Number(calculation.summary.pendingResources || 0) > 0 && (
                  <Alert
                    type="warning"
                    showIcon
                    message={`${Number(calculation.summary.pendingResources)} recursos mantidos sem preço nesta etapa`}
                    description="Somente categorias ainda sem contrato de consumo reconhecido permanecem registradas sem preço."
                  />
                )}
              </Card>
            )}
            <Row gutter={12}>
              <Col span={6}><Statistic title="Recursos no arquivo" value={selectedAssessment.inventorySummary?.total || 0} /></Col>
              <Col span={6}><Statistic title="Recursos calculáveis" value={selectedAssessment.resourcesStored || 0} /></Col>
              <Col span={6}><Statistic title="Cobertura do custo atual" value={selectedAssessment.inventorySummary?.financialCoveragePercent || 0} suffix="%" precision={2} /></Col>
              <Col span={6}><Statistic title="Custo atual identificado" value={selectedAssessment.inventorySummary?.officialPeriodCost || 0} precision={2} prefix={selectedAssessment.currency === 'USD' ? 'US$' : 'R$'} /></Col>
            </Row>
            {selectedAssessment.inventorySummary && (
              <Row gutter={12}>
                <Col span={4}><Statistic title="Diretos" value={selectedAssessment.inventorySummary.direct || 0} /></Col>
                <Col span={4}><Statistic title="Por serviço" value={selectedAssessment.inventorySummary.service || 0} /></Col>
                <Col span={4}><Statistic title="Dependentes" value={selectedAssessment.inventorySummary.dependent || 0} /></Col>
                <Col span={4}><Statistic title="Não faturáveis" value={selectedAssessment.inventorySummary.nonBillable || 0} /></Col>
                <Col span={4}><Statistic title="Sem regra" value={selectedAssessment.inventorySummary.unsupported || 0} /></Col>
                <Col span={4}><Statistic title="Tipos encontrados" value={selectedAssessment.inventorySummary.resourceTypes || 0} /></Col>
              </Row>
            )}
            {(selectedAssessment.targetProviders || []).includes('tencent') && (
              <Alert
                type="info"
                showIcon
                message={`Desconto comercial Tencent: ${Number(selectedAssessment.commercialTerms?.tencentDiscountPercent || 0).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}%`}
                description="Aplicado somente ao total da Tencent Cloud, preservando o preço público no resultado."
              />
            )}
            {Object.values(selectedAssessment.commercialTerms?.tencentChannelDiscounts || {}).some(value => Number(value) > 0) && (
              <Alert
                type="info"
                showIcon
                message="Condições comerciais via parceria Tencent"
                description={
                  <Space wrap>
                    {(['aws', 'azure', 'gcp'] as Provider[])
                      .filter(provider => Number(selectedAssessment.commercialTerms?.tencentChannelDiscounts?.[provider] || 0) > 0)
                      .map(provider => (
                        <Tag key={provider}>
                          {providerLabels[provider]}: {Number(selectedAssessment.commercialTerms.tencentChannelDiscounts[provider]).toLocaleString('pt-BR')}%
                        </Tag>
                      ))}
                  </Space>
                }
              />
            )}
            <Alert
              type="info"
              showIcon
              message={selectedAssessment.regionMode === 'custom' ? 'Regiões personalizadas' : 'Geografia equivalente'}
              description={
                <Space wrap>
                  {(selectedAssessment.targetProviders || []).map((provider: Provider) => (
                    <Tag key={provider}>
                      {providerLabels[provider]}: {selectedAssessment.regionMode === 'custom'
                        ? selectedAssessment.targetRegions?.[provider]
                        : equivalentRegions[selectedAssessment.geography || 'brazil']?.[provider]}
                    </Tag>
                  ))}
                </Space>
              }
            />
            {selectedAssessment.resourcePartitions?.length > 0 && (
              <div className="commercial-assessments__resource-filters">
                <Input.Search
                  allowClear
                  placeholder="Buscar nome, ID, projeto ou tipo"
                  value={detailSearch}
                  onChange={event => setDetailSearch(event.target.value)}
                  onSearch={value => loadDetailResources(selectedAssessment.id, 1, detailPagination.limit, value, detailClass, detailCategory)}
                />
                <Select
                  allowClear
                  placeholder="Classe de comparação"
                  value={detailClass}
                  onChange={value => {
                    setDetailClass(value);
                    loadDetailResources(selectedAssessment.id, 1, detailPagination.limit, detailSearch, value, detailCategory);
                  }}
                  options={[
                    { value: 'direct', label: 'Comparação direta' },
                    { value: 'service', label: 'Comparação por serviço' },
                    { value: 'dependent', label: 'Dependente' },
                    { value: 'non-billable', label: 'Não faturável' },
                    { value: 'unsupported', label: 'Sem regra' }
                  ]}
                />
                <Select
                  allowClear
                  placeholder="Categoria"
                  value={detailCategory}
                  onChange={value => {
                    setDetailCategory(value);
                    loadDetailResources(selectedAssessment.id, 1, detailPagination.limit, detailSearch, detailClass, value);
                  }}
                  options={Object.keys(selectedAssessment.inventorySummary?.categoryCounts || {}).map(value => ({ value, label: value }))}
                />
              </div>
            )}
            <Table
              size="small"
              rowKey={(row: NormalizedResource) => `${row.projectId}-${row.sourceResourceId}`}
              dataSource={detailResources}
              pagination={{
                current: detailPagination.page,
                pageSize: detailPagination.limit,
                total: detailPagination.total,
                showSizeChanger: true,
                pageSizeOptions: [25, 50, 100, 200],
                showTotal: total => `${total} recursos`
              }}
              onChange={pagination => loadDetailResources(
                selectedAssessment.id,
                pagination.current || 1,
                pagination.pageSize || 50,
                detailSearch,
                detailClass,
                detailCategory
              )}
              scroll={{ x: 1100, y: 480 }}
              columns={[
                { title: 'Recurso', dataIndex: 'name', key: 'name', width: 240 },
                { title: 'Categoria', dataIndex: 'category', key: 'category', width: 130 },
                { title: 'Comparação', dataIndex: 'comparisonClass', key: 'comparisonClass', width: 140, render: value => <Tag>{value}</Tag> },
                { title: 'Projeto', dataIndex: 'projectId', key: 'projectId', width: 180 },
                { title: 'Região', dataIndex: 'region', key: 'region', width: 150 },
                { title: 'Máquina', dataIndex: 'machineType', key: 'machineType', width: 180, render: value => value || '—' },
                { title: 'SO', dataIndex: 'operatingSystem', key: 'operatingSystem', width: 100, render: value => value || '—' },
                { title: 'vCPU', dataIndex: 'vcpu', key: 'vcpu', width: 80, render: value => value ?? '—' },
                { title: 'Memória', dataIndex: 'memoryGb', key: 'memoryGb', width: 100, render: value => value == null ? '—' : `${value} GB` },
                { title: 'Disco', dataIndex: 'diskSizeGb', key: 'diskSizeGb', width: 100, render: value => value == null ? '—' : `${value} GB` },
                { title: 'Custo atual', dataIndex: 'currentMonthlyCost', key: 'currentMonthlyCost', width: 120, render: value => value == null ? '—' : Number(value).toLocaleString('pt-BR', { style: 'currency', currency: selectedAssessment.currency || 'BRL' }) }
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CommercialAssessments;
